import { useState, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Navbar from './components/Navbar'
import Hero from './components/Hero'
import JourneyFork from './components/JourneyFork'
import Philosophy from './components/Philosophy'
import Transformations from './components/Transformations'
import Credibility from './components/Credibility'
import Process from './components/Process'
import WhyUs from './components/WhyUs'
import Testimonials from './components/Testimonials'
import Pricing from './components/Pricing'
import GrowthPlans from './components/GrowthPlans'
import Discoverability from './components/Discoverability'
import Resources from './components/Resources'
import RecruiterToolkit from './components/RecruiterToolkit'
import FAQ from './components/FAQ'
import FinalCTA from './components/FinalCTA'
import Footer from './components/Footer'
import { useViewMode } from './context/ViewModeContext'
import { useFormModal } from './context/FormModalContext'

// Everything below is lazy — checkpoint 113. Only "/" (Home, built from the
// eager imports above) needs to be in the initial JS a visitor downloads;
// every other route is its own chunk, fetched only when that route is
// actually visited. Previously the whole site (86 routes, every page
// component) shipped as one ~980KB bundle regardless of which single page
// someone landed on — this is what PageSpeed Insights was flagging as
// "unused JavaScript". Prerendering still gives every route real, complete
// static HTML on first load (see entry-server.jsx's switch to
// renderToPipeableStream) — this only changes how much JS a visitor's
// browser has to fetch and execute, not what search engines or a
// no-JS visitor see.
const MeetBee = lazy(() => import('./components/MeetBee'))
const NotFound = lazy(() => import('./components/NotFound'))
const WebsitesThatSell = lazy(() => import('./pages/campaigns/WebsitesThatSell'))
const BusinessWebsites = lazy(() => import('./pages/campaigns/BusinessWebsites'))
const EventWebsites = lazy(() => import('./pages/campaigns/EventWebsites'))
const WebsiteRedesign = lazy(() => import('./pages/campaigns/WebsiteRedesign'))
const FunnelPages = lazy(() => import('./pages/campaigns/FunnelPages'))
const LandingPages = lazy(() => import('./pages/campaigns/LandingPages'))
const BookLaunch = lazy(() => import('./pages/campaigns/BookLaunch'))
const Guide = lazy(() => import('./pages/Guide'))
const GetAWebsite = lazy(() => import('./pages/GetAWebsite'))
const OnlineGrowth = lazy(() => import('./pages/OnlineGrowth'))
const Partners = lazy(() => import('./pages/Partners'))
const Insights = lazy(() => import('./pages/Insights'))
const InsightArticle = lazy(() => import('./pages/InsightArticle'))
const ThankYou = lazy(() => import('./pages/ThankYou'))
const Work = lazy(() => import('./pages/Work'))
const Services = lazy(() => import('./pages/Services'))
const PricingPage = lazy(() => import('./pages/Pricing'))
const FaqPage = lazy(() => import('./pages/Faq'))
const About = lazy(() => import('./pages/About'))
const Industries = lazy(() => import('./pages/Industries'))
const Locations = lazy(() => import('./pages/Locations'))
const Academy = lazy(() => import('./pages/Academy'))
const AcademyProgramme = lazy(() => import('./pages/AcademyProgramme'))
const PartnerOnboarding = lazy(() => import('./pages/PartnerOnboarding'))
const PartnerRefer = lazy(() => import('./pages/PartnerRefer'))
const Audit = lazy(() => import('./pages/Audit'))
const Calculator = lazy(() => import('./pages/Calculator'))
const Workshop = lazy(() => import('./pages/Workshop'))
const SearchPage = lazy(() => import('./pages/SearchPage'))

// Kept minimal and non-flashy on purpose: this only ever shows during a
// prerendering wait (renderToPipeableStream, resolved before the HTML is
// written — never seen by a real visitor there) or, on the client, for the
// brief moment between a click and a lazy chunk finishing its fetch on a
// slow connection. No spinner/animation so it can't itself cause a layout
// jump or distract on fast connections where it's invisible in practice.
function RouteFallback() {
  return <div style={{ minHeight: '60vh', background: 'var(--bg)' }} />
}

function Home() {
  const [selectedTier, setSelectedTier] = useState(null)
  const [selectedGrowthPlan, setSelectedGrowthPlan] = useState(null)
  const { mode } = useViewMode()
  const { openInquiry } = useFormModal()
  const isPortfolio = mode === 'portfolio'

  // Picking a plan (Pricing) or a growth plan (GrowthPlans) now pops the
  // SideShift-style modal straight open, pre-filled with that pick as
  // step 1, instead of scrolling down to an inline form — same
  // conversion, no scroll.
  const handleSelectTier = (tierId) => {
    setSelectedTier(tierId)
    openInquiry({ selectedTier: tierId, onSelectTier: setSelectedTier, selectedGrowthPlan })
  }
  const handleSelectGrowthPlan = (planId) => {
    setSelectedGrowthPlan(planId)
    openInquiry({ selectedTier, onSelectTier: setSelectedTier, selectedGrowthPlan: planId })
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Navbar />
      <main>
        <Hero />
        <JourneyFork />
        {isPortfolio ? (
          // Portfolio Mode: lead with proof, then the ask (pricing/growth
          // plans) right after — since it now opens a quick popup instead
          // of a long inline form, there's no reason to bury it below
          // several more trust sections — then the rest of the trust-
          // building content for anyone still deciding.
          <>
            <Testimonials />
            <Transformations />
            <Pricing onSelect={handleSelectTier} />
            <Discoverability />
            <GrowthPlans onSelect={handleSelectGrowthPlan} />
            <Process />
            <WhyUs />
            <Credibility />
            <RecruiterToolkit />
            <Philosophy />
            <Resources />
          </>
        ) : (
          // Business Mode IA, reordered to track the questions a visitor
          // actually asks, in order, rather than the order these sections
          // happened to be built in:
          // 1. Real, independent reviews right up top — proof before anything else (Testimonials)
          // 2. "Have you done this before?" → proof next (Transformations)
          // 3/4. The ask itself, right after proof — Pricing, Discoverability
          //    (bridges into GrowthPlans), GrowthPlans. Popping a plan open
          //    a modal now costs a visitor nothing, so there's no reason to
          //    make them scroll through five more sections to reach it.
          // 5. "What's different about how you think?" → WhyUs
          // 6. "What's your approach, generally?" → Philosophy
          // 7. "What actually happens if I hire you?" → Process
          // 8. More trust reinforcement → Credibility
          // 9+. Read more, handle objections → Resources, FAQ, FinalCTA
          <>
            <Testimonials />
            <Transformations />
            <Pricing onSelect={handleSelectTier} />
            <Discoverability />
            <GrowthPlans onSelect={handleSelectGrowthPlan} />
            <WhyUs />
            <Philosophy />
            <Process />
            <Credibility />
            <Resources />
          </>
        )}
        <FAQ />
        <FinalCTA
          onCtaClick={() => openInquiry({ selectedTier, onSelectTier: setSelectedTier, selectedGrowthPlan })}
        />
      </main>
      <Footer />
    </div>
  )
}

// This tree is rendered by BOTH the client (App.jsx, inside BrowserRouter)
// and the prerender script (entry-server.jsx, inside StaticRouter). Any
// route added here needs a matching entry in scripts/routes.js so it
// actually gets prerendered — see that file's comment for why.
export default function AppRoutes() {
  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/meet-bee" element={<MeetBee />} />
        <Route path="/campaigns/websites-that-sell" element={<WebsitesThatSell />} />
        <Route path="/campaigns/business-websites" element={<BusinessWebsites />} />
        <Route path="/campaigns/event-websites" element={<EventWebsites />} />
        <Route path="/campaigns/website-redesign" element={<WebsiteRedesign />} />
        <Route path="/campaigns/funnel-pages" element={<FunnelPages />} />
        <Route path="/campaigns/landing-pages" element={<LandingPages />} />
        <Route path="/campaigns/book-launch" element={<BookLaunch />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/getawebsite" element={<GetAWebsite />} />
        <Route path="/onlinegrowth" element={<OnlineGrowth />} />
        <Route path="/partner" element={<Partners />} />
        <Route path="/partner/onboarding" element={<PartnerOnboarding />} />
        <Route path="/partner/refer" element={<PartnerRefer />} />
        {/* Old URL kept as a redirect — anything that already links to
            /partners (bookmarks, past shares) still lands correctly. */}
        <Route path="/partners" element={<Navigate to="/partner" replace />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/:slug" element={<InsightArticle />} />
        {/* Real, permanent, crawlable case-study pages — reuses the same
            CaseStudy component the homepage opens as a modal, but as a
            proper routed page with its own URL. This is what turns each
            project's walkthrough video into a legitimate "watch page" for
            Google's video indexer, and makes the case-study writeups
            themselves indexable content for the first time. */}
        <Route path="/work/:slug" element={<Work />} />
        <Route path="/services" element={<Services />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="/academy" element={<Academy />} />
        <Route path="/academy/ai-business-builder" element={<AcademyProgramme />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/calculator" element={<Calculator />} />
        <Route path="/workshop" element={<Workshop />} />
        {/* Phase 4 search pages — Step 7A (service + industry) and
            Step 7C (commercial comparison) both resolve through the
            same SearchPage template, looked up by slug in
            src/data/searchPages.js. Two URL prefixes for clean, honest
            paths: /website-design/* reads as a service page,
            /compare/* reads as a buying-guide page. */}
        <Route path="/website-design/:slug" element={<SearchPage />} />
        <Route path="/compare/:slug" element={<SearchPage />} />
        {/* Conversion destination only — deliberately not linked from nav,
            footer, or scripts/routes.js (no prerendered HTML, no sitemap
            entry) and disallowed in robots.txt, same treatment as
            /meet-bee. Netlify's SPA catch-all (_redirects) still serves it
            on direct visit, which is all a form redirect / Calendly
            confirmation redirect / Google Ads destination needs. */}
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  )
}
